// Noyau — décisions pédagogiques pilotées par les DONNÉES des packs (CONTRACT §9) :
// sondes (catalogue fermé du noyau), conseils (`engine.advice`), outils à choix (`engine.tools`),
// clavier contextuel (`engine.keyboard`). Aucun type de nœud en dur : rôles lus dans `eval` / `print`.

import { parse, toText } from './syntax.js';
import { isLeaf } from './ast.js';
import { light, keyOf, roles, prepareRule, match, rewriteAll, instantiate } from './rewrite.js';
import { evaluate, toF, relationParts, exactValue, vectorPoints } from './equiv.js';
import { num, Q, symbols } from './ast.js';

// ── 9.1 Sondes ────────────────────────────────────────────────────────────
function pre(n, f) {
  f(n);
  if (!isLeaf(n)) n.k.forEach((c) => pre(c, f));
}
const numVal = (n, table) => {
  const v = toF(evaluate(n, {}, table));
  return Number.isFinite(v) ? Math.round(v * 1e9) / 1e9 : null;
};
function partsOfDivs(ast, table, i) {
  const R = roles(table),
    all = [];
  pre(ast, (n) => {
    if (n.t === R.div && n.k.length === 2) {
      const v = numVal(n.k[i], table);
      if (v !== null) all.push(Math.abs(v));
    }
  });
  const values = [...new Set(all)];
  return { values, distinct: values.length, count: all.length };
}
export const PROBES = {
  denominators: (ast, table) => partsOfDivs(ast, table, 1),
  numerators: (ast, table) => partsOfDivs(ast, table, 0),
  relation: (ast, table) => {
    const rp = relationParts(ast, table);
    return rp ? { op: ast.t, sides: ast.k.length } : { op: null, sides: 0 };
  },
  // La ligne, chaque relation d'ordre remplacée par son `flip` (table : Lt↔Gt, Le↔Ge), devient-elle
  // équivalente à la ligne d'où elle vient ? (sens oublié en multipliant / divisant par un négatif)
  // ctx = { from: AST de la ligne d'origine, equiv: (a, b) => bool } fourni par le juge.
  flip: (ast, table, ctx) => {
    let changed = false;
    const sw = (n) => {
      if (isLeaf(n)) return n;
      const f = table.nodes[n.t]?.relation ? table.nodes[n.t]?.flip : null;
      if (f) changed = true;
      return { ...n, t: f || n.t, k: n.k.map(sw) };
    };
    const flipped = sw(ast);
    if (!changed || !ctx?.from || !ctx.equiv) return { fixes: false };
    // « sens oublié » : l'élève a GARDÉ les relations d'ordre de la ligne d'origine (s'il les a changées
    // sans raison, c'est une autre erreur : −8x + 2 ≤ −14 depuis −5x + 2 ≥ 3x − 14 n'est pas un oubli).
    const ordered = (n, out = []) => {
      if (!isLeaf(n)) {
        if (table.nodes[n.t]?.relation && table.nodes[n.t]?.flip) out.push(n.t);
        n.k.forEach((c) => ordered(c, out));
      }
      return out;
    };
    if (ordered(ast).sort().join() !== ordered(ctx.from).sort().join()) return { fixes: false };
    let fixes = false;
    try {
      fixes = !!ctx.equiv(ctx.from, flipped);
    } catch {
      fixes = false;
    }
    return { fixes };
  },
};
export function probe(name, ast, table, ctx) {
  return PROBES[name] ? PROBES[name](ast, table, ctx) : null;
}

// ── 9.2 Conseils ──────────────────────────────────────────────────────────
// Gabarit : {champ} ; un tableau s'écrit « 9 et 25 » / « 6, 9 et 25 ».
export function renderTemplate(tpl, facts) {
  return String(tpl).replace(/\{(\w+)\}/g, (m, f) => {
    const v = facts?.[f];
    if (Array.isArray(v))
      return v.length <= 1 ? v.join('') : `${v.slice(0, -1).join(', ')} et ${v.at(-1)}`;
    return v === undefined || v === null ? m : String(v);
  });
}
// Ajoute `advice: [{ id, message }]` au verdict et concatène les messages à `verdict.message`.
export function applyAdvice(engine, verdict, ctx) {
  const list = engine.advice || [];
  if (!list.length || !verdict.ast) return verdict;
  const table = engine.table;
  const ids = new Set((verdict.gestures || []).map((g) => g.ruleId));
  const out = [];
  for (const a of list) {
    if (a.on?.length && !a.on.includes(verdict.status)) continue;
    if (a.after?.length && !a.after.some((r) => ids.has(r))) continue;
    const facts = a.probe ? probe(a.probe, verdict.ast, table, ctx) : {};
    if (!facts) continue;
    let ok = true;
    if (a.when) {
      try {
        ok = !!a.when(facts);
      } catch {
        ok = false;
      }
    }
    if (ok) out.push({ id: a.id, message: renderTemplate(a.message, facts) });
  }
  if (!out.length) return verdict;
  return {
    ...verdict,
    advice: out,
    message: [verdict.message, ...out.map((x) => x.message)].filter(Boolean).join(' '),
  };
}

// ── 9.3 Outils à choix ────────────────────────────────────────────────────
export function toolsFor(engine, nodeType) {
  return (engine.tools || [])
    .filter((t) => t.target === nodeType)
    .map((t) => ({ key: t.key, label: t.label, kind: t.kind }));
}

const gcdI = (a, b) => {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
};
const isPosInt = (x) => Number.isFinite(x) && Math.abs(x - Math.round(x)) < 1e-9;

// ── Outil « rewrite » : appliquer une identité du cours à l'écriture touchée ────────────────────
// Les propositions SORTENT DES PACKS : le rhs de chaque règle dont le motif colle au nœud touché —
// l'écriture juste (les règles du geste de l'outil) et les erreurs classiques (les pièges, qui
// partagent le même lhs : « ?k × (?a + ?b) » donne « ?k × ?a + ?k × ?b » et « ?k × ?a + ?b »).
// L'outil n'invente aucun leurre et ne dit pas lequel est juste : l'élève choisit, le juge tranche.
// Une seule écriture par règle : la PREMIÈRE liaison suit l'ordre de la ligne (le rhs est instancié
// brut, pas en forme légère, pour écrire « 4 × x + 4 × 2 » et non « x × 4 + 2 × 4 »).
// « −(−y) » s'écrit « y » : le rhs instancié peut empiler deux signes (« −?b » avec ?b = −y). Pliage
// PUREMENT d'écriture — aucun calcul n'est fait (« 4 × 2 » reste « 4 × 2 »).
function unNeg(n, R) {
  if (isLeaf(n)) return n;
  if (n.t === R.neg && n.k[0]?.t === R.neg) return unNeg(n.k[0].k[0], R);
  return { ...n, k: n.k.map((c) => unNeg(c, R)) };
}

const fnv = (t) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < t.length; i++) h = Math.imul(h ^ t.charCodeAt(i), 0x01000193) >>> 0;
  return h;
};

/** un « 1 × » ou « −1 × » qui n'existait pas dans l'écriture touchée */
const ONE_TIMES = /(^|[\s(−])1 ×/;

export function rewriteChoices(engine, tool, lineText, targetPath) {
  const table = engine.table;
  let ast;
  try {
    ast = parse(lineText, table);
  } catch {
    return null;
  }
  let node = ast;
  for (const i of targetPath || []) {
    node = node?.k?.[i];
    if (!node) return null;
  }
  if (!node || isLeaf(node)) return null;
  const out = [];
  const seen = new Set();
  for (const rule of engine.rules || []) {
    const ofGesture = rule.gesture === tool.gesture;
    if (!ofGesture && rule.kind !== 'trap') continue;
    if (!rule.lhs || !rule.rhs) continue;
    const P = prepareRule(rule, table);
    // même forme que l'écriture touchée : une règle « ?k × (?a + ?b) » ne s'invite pas sur « −(5 − y) »
    // en lisant le « − » comme un facteur (−1), qui donnerait « −1 × 5 + −1 × (−y) »
    if (!P.lhs || (P.lhs.t !== 'Var' && P.lhs.t !== node.t)) continue;
    let bs = [];
    try {
      bs = match(P.lhs, node, table);
    } catch {
      bs = [];
    }
    if (!bs.length) continue;
    let text;
    try {
      text = toText(unNeg(instantiate(rule.rhs, bs[0]), roles(table)), table);
    } catch {
      continue;
    }
    const src = toText(node, table);
    if (!text || text === src || seen.has(text)) continue;
    // un « − » lu comme un facteur −1 (« x × 4 − x × 3 » apparié à « ?k × ?a + ?k × ?b ») donne
    // « x(4 + x × (−1 × 3)) » : une écriture que personne n'écrit, on ne la propose pas
    if (ONE_TIMES.test(text) && !ONE_TIMES.test(src)) continue;
    seen.add(text);
    out.push({ text, right: ofGesture });
  }
  if (!out.some((o) => o.right)) return null; // aucune identité à appliquer ici
  // ordre stable mais imprévisible : la bonne écriture n'est jamais à la même place
  out.sort((a, b) => fnv(a.text) - fnv(b.text));
  const size = tool.choices?.size ?? 4;
  const kept = out.filter((o) => o.right).slice(0, 1);
  for (const o of out) if (!kept.includes(o) && kept.length < size) kept.push(o);
  kept.sort((a, b) => fnv(a.text) - fnv(b.text));
  return { label: tool.label, values: kept.map((o) => o.text) };
}

// toolChoices(engine, st, toolKey, lineText, targetPath) → { label, values } (ou null)
//   expected : obtenu en filtrant le rhs de la règle de l'outil sur la ligne attendue du corrigé
//   (`expectedText`, fourni par judge.js via progress) : la liaison dont ?a / ?b (hors signe) sont le
//   numérateur / dénominateur de la cible donne la valeur du paramètre.
export function toolChoicesFor(engine, tool, lineText, targetPath, expectedText) {
  if (tool.kind === 'rewrite') return rewriteChoices(engine, tool, lineText, targetPath);
  if (tool.kind === 'value') return valueChoices(engine, tool, lineText);
  if (tool.kind === 'signs' || tool.kind === 'neutral') return { label: tool.label, values: [] }; // rien à proposer : l'élève choisit chaque signe
  const table = engine.table,
    R = roles(table);
  let ast;
  try {
    ast = parse(lineText, table);
  } catch {
    return null;
  }
  let node = ast;
  for (const i of targetPath || []) {
    node = node?.k?.[i];
    if (!node) return null;
  }
  if (node.t === R.neg && node.k[0].t === tool.target) node = node.k[0];
  if (node.t !== tool.target || !node.k || node.k.length !== 2) return null;
  const [numN, denN] = node.k;
  const ownDen = Math.abs(numVal(denN, table) ?? NaN);
  const dens = [],
    nums = [];
  pre(ast, (n) => {
    if (n.t === R.div && n.k.length === 2) {
      const d = numVal(n.k[1], table),
        u = numVal(n.k[0], table);
      if (d !== null && n !== node) dens.push(Math.abs(d));
      if (u !== null) nums.push(Math.abs(u));
    }
  });
  const allDens = [ownDen, ...dens].filter((x) => isPosInt(x) && x > 0);
  const lcm = allDens.length > 1 ? allDens.reduce((L, x) => (L * x) / gcdI(L, x)) : null;

  // valeur attendue du paramètre pour CETTE fraction
  const expected = [];
  const rule = engine.rules.find((r) => r.id === tool.rule || r.key === tool.rule);
  if (rule && expectedText) {
    try {
      const exp = light(parse(expectedText, table), table);
      const P = prepareRule(rule, table);
      const absKey = (x) => {
        const L = light(x, table);
        return keyOf(L.t === R.neg ? L.k[0] : L);
      };
      const kn = absKey(numN),
        kd = absKey(denN);
      pre(exp, (s) => {
        for (const b of match(P.rhs, s, table)) {
          const va = P.lhs?.k?.[0]?.t === 'Var' ? P.lhs.k[0].name : 'a';
          const vb = P.lhs?.k?.[1]?.t === 'Var' ? P.lhs.k[1].name : 'b';
          const pa = b[va],
            pb = b[vb];
          if (!pa || !pb || !b[tool.param]) continue;
          if (absKey(pa) === kn && absKey(pb) === kd) {
            const v = numVal(b[tool.param], table);
            if (v !== null && !expected.includes(Math.abs(v))) expected.push(Math.abs(v));
          }
        }
      });
    } catch {
      /* pas de valeur attendue */
    }
  }

  const C = tool.choices || {};
  const size = C.size ?? 5;
  const useful = [],
    pool = [];
  const ok = (k) => isPosInt(k) && k > 1 && k <= 100;
  const add = (k) => {
    if (ok(k)) {
      k = Math.round(k);
      if (!pool.includes(k) && !useful.includes(k)) pool.push(k);
    }
  };
  for (const src of C.sources || []) {
    if (src === 'expected') {
      for (const k of expected)
        if (ok(k) && !useful.includes(Math.round(k))) useful.push(Math.round(k));
    } else if (src === 'own_denominator') add(ownDen);
    else if (src === 'other_denominators') dens.forEach(add);
    else if (src === 'lcm_ratio') {
      if (lcm && ownDen) add(lcm / ownDen);
    } else if (src === 'lcm') {
      if (lcm) add(lcm);
    } else if (src === 'numerators') nums.forEach(add);
    else if (src.startsWith('small:')) {
      const [a, b] = src.slice(6).split('-').map(Number);
      if (b === undefined) add(a);
      else for (let k = a; k <= b && pool.length + useful.length < size; k++) add(k);
    }
  }
  pool.splice(0, pool.length, ...pool.filter((k) => !useful.includes(k)));
  const keep = C.keepExpected === false ? [] : useful.slice(0, Math.max(1, size - 2));
  const values = [...keep, ...pool.slice(0, size - keep.length)];
  if (C.sort !== 'none') values.sort((a, b) => (C.sort === 'desc' ? b - a : a - b));
  return { label: tool.label, values };
}

// ── 9.4 Clavier contextuel ────────────────────────────────────────────────
// Atomes d'une ligne, écrits comme l'éditeur les écrit (portage générique de astToTokens de l'UI,
// piloté par les stratégies d'impression de la table) : nombres, lettres, opérations, fraction, parenthèses.
export function atomsOf(
  n,
  table,
  acc = { nums: new Set(), ids: new Set(), ops: new Set(), frac: false, parens: false },
) {
  const R = roles(table);
  const go = (n, pp) => {
    const spec = table.nodes[n.t];
    const prec = n.t === 'Num' || n.t === 'Sym' ? 100 : (spec?.prec ?? 100);
    const negPrec = table.nodes[R.neg]?.prec ?? 21;
    if (n.t === 'Num') {
      if (n.dec) acc.nums.add(n.dec.replace(/^[-−]/, ''));
      else if (n.q === 1) acc.nums.add(String(Math.abs(n.p)));
      else {
        acc.frac = true;
        acc.nums.add(String(Math.abs(n.p)));
        acc.nums.add(String(n.q));
      }
      if (n.p < 0) {
        acc.ops.add('−');
        if (pp > negPrec) acc.parens = true;
      }
      return;
    }
    if (n.t === 'Sym') {
      acc.ids.add(n.name);
      return;
    }
    switch (spec?.print) {
      case 'sum':
        n.k.forEach((c, i) => {
          if (i === 0) return go(c, prec);
          if (c.t === R.neg) {
            acc.ops.add('−');
            return go(c.k[0], prec + 0.5);
          }
          if (c.t === 'Num' && c.p < 0) {
            acc.ops.add('−');
            return go({ ...c, p: -c.p, dec: c.dec?.replace(/^[-−]/, '') }, prec + 0.5);
          }
          acc.ops.add('+');
          go(c, prec);
        });
        break;
      case 'product':
        n.k.forEach((c, i) => {
          const neg = c.t === R.neg || (c.t === 'Num' && c.p < 0);
          if (i && neg) {
            acc.parens = true;
            go(c, 0);
          } else go(c, prec);
          if (i === 0) return;
          const a = n.k[i - 1];
          const leftOk = (a.t === 'Num' && a.p > 0 && a.q === 1 && !a.dec) || a.t === 'Sym';
          const grouped =
            (table.nodes[c.t]?.prec ?? 100) < prec && table.nodes[c.t]?.eval !== 'div' && !neg;
          const juxta =
            grouped ||
            (leftOk &&
              ((c.t === 'Sym' && /^\p{L}/u.test(c.name) && !(a.t === 'Sym' && a.name === c.name)) ||
                (c.t === R.pow && c.k[0].t === 'Sym')));
          if (!juxta) acc.ops.add('×');
        });
        break;
      case 'power': {
        const [b, e] = n.k;
        go(b, prec + 0.5);
        // un exposant ² / ³ est un nombre écrit : le 2 du double produit 2ab se compose avec lui
        if (e.t === 'Num' && e.q === 1 && (e.p === 2 || e.p === 3)) {
          acc.ops.add(e.p === 2 ? '²' : '³');
          acc.nums.add(String(e.p));
        } else {
          acc.ops.add('^');
          acc.parens = true;
          go(e, 0);
        }
        break;
      }
      case 'prefix':
        acc.ops.add('−');
        go(n.k[0], spec.operandPrec ?? prec);
        break;
      case 'infix':
        n.k.forEach((c, i) => {
          if (i) acc.ops.add(spec.text.trim());
          go(c, i ? prec + 0.5 : prec);
        });
        break;
      case 'call':
        acc.ids.add(n.name);
        acc.parens = true;
        n.k.forEach((c, i) => {
          if (i) acc.ops.add(';');
          go(c, 0);
        });
        break;
      default:
        if (spec?.eval === 'div') {
          let [top, bot] = n.k,
            neg = false;
          if (top.t === 'Num' && top.p < 0) {
            top = { ...top, p: -top.p, dec: top.dec?.replace(/^[-−]/, '') };
            neg = true;
          } else if (table.nodes[top.t]?.print === 'prefix') {
            top = top.k[0];
            neg = true;
          }
          acc.frac = true;
          // une somme au dénominateur s'écrit entre parenthèses dans l'éditeur
          if (table.nodes[bot.t]?.eval === 'add') acc.parens = true;
          go(top, 0);
          go(bot, 0);
          if (neg) {
            acc.ops.add('−');
            if (pp > negPrec) acc.parens = true;
          }
          return;
        }
        if (spec?.eval === 'sqrt') {
          acc.ops.add('√');
          acc.parens = true;
          go(n.k[0], 0);
          return;
        }
        // dérivée (u)′ : la marque de dérivée et des parenthèses
        if (spec?.eval === 'derive') {
          acc.ops.add(table.lex.prime ?? '′');
          acc.parens = true;
          go(n.k[0], 0);
          return;
        }
        // côté d'approche (3⁺, 0⁻) : sa marque au clavier
        if (spec?.eval === 'right' || spec?.eval === 'left') {
          acc.ops.add(spec.text.replace('$0', ''));
          go(n.k[0], 100);
          return;
        }
        n.k.forEach((c) => go(c, prec));
    }
    if (prec < pp) acc.parens = true;
  };
  go(n, 0);
  return acc;
}

const OP_ORDER = [
  '+',
  '−',
  '×',
  '÷',
  '²',
  '³',
  '^',
  '′',
  '⁺',
  '⁻',
  '=',
  '≤',
  '≥',
  '<',
  '>',
  '≠',
  'ou',
  'et',
  '√',
  ';',
];
// palette : { ids, numbers, ops, frac, parens } — expectedText (progress) et lastAst (dernière ligne gardée ou départ)
// Morceaux : sous-expressions de ce que l'élève a sous les yeux (départ, dernière ligne gardée), pour composer
// plus vite. Jamais tirées du corrigé (ce serait souffler la réponse). Groupes parenthésés, puissances,
// produits, fractions, fonctions, et chaque membre d'une relation (« v − v₀ » dans v − v₀ = at) ;
// ni les feuilles, ni la ligne entière.
export function chunksOf(engine, asts, max = 8) {
  const table = engine.table;
  const ev = (n) => table.nodes[n.t]?.eval;
  const size = (n) => (isLeaf(n) ? 1 : 1 + n.k.reduce((a, c) => a + size(c), 0));
  const out = [],
    seen = new Set();
  const add = (n, wrap) => {
    if (isLeaf(n) || size(n) < 3 || size(n) > 15) return;
    let t;
    try {
      t = toText(n, table);
    } catch {
      return;
    }
    if (wrap) t = `(${t})`;
    if (!seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  const visit = (n, parent, top) => {
    if (isLeaf(n)) return;
    const e = ev(n);
    const isRel = !!relationParts(n, table) || table.nodes[n.t]?.connective;
    if (isRel && relationParts(n, table))
      n.k.forEach((c) => {
        if (ev(c) === 'add') add(c, false);
      }); // un membre entier
    if (!isRel && !top) {
      const pe = parent && ev(parent);
      if (e === 'add' && (pe === 'mul' || pe === 'pow' || pe === 'neg' || pe === 'div'))
        add(n, pe !== 'div');
      else if (
        e === 'pow' ||
        e === 'div' ||
        e === 'sqrt' ||
        e === 'call' ||
        (e === 'mul' && n.k.some((c) => !isLeaf(c) || c.t === 'Sym'))
      )
        add(n, false);
    }
    n.k.forEach((c) => visit(c, n, isRel && top ? false : false));
  };
  for (const a of asts) if (a) visit(a, null, true);
  return out.slice(0, max);
}

// lineAsts : toutes les lignes affichées sur la feuille (départ compris), dans l'ordre d'écriture.
// pathAsts : toutes les lignes du corrigé (keyboard.frac = 'path' : la fraction dès qu'il en a une, n'importe où).
// sourceAst : la ligne « source » des bouts (par défaut la dernière ligne ; l'élève peut en choisir une plus ancienne,
// ou le départ) — keyboard.chunks.from = ["source"], keyboard.numbers = "source".
export function paletteFor(
  engine,
  model,
  expectedText,
  lastAst,
  startAst = null,
  lineAsts = [],
  pathAsts = [],
  sourceAst = null,
) {
  const table = engine.table;
  const K = engine.keyboard || {};
  const always = K.always ?? ['−'],
    withMinus = K.withMinus ?? ['+'],
    sort = K.sort ?? 'asc';
  const want = { nums: new Set(), ids: new Set(), ops: new Set(), frac: false, parens: false };
  if (expectedText) {
    try {
      atomsOf(parse(expectedText, table), table, want);
    } catch {
      /* ligne illisible */
    }
  }
  const seen = lastAst ? atomsOf(lastAst, table) : { nums: new Set(), ids: new Set() };
  const nv = (s) => parseFloat(String(s).replace(',', '.'));
  // les données de l'énoncé (symboles « given » : value, exact) sont sous les yeux de l'élève dès le départ
  const given = new Set();
  for (const sym of Object.values(model?.symbols || {})) {
    if (!sym?.given) continue;
    if (sym.value != null && Number.isFinite(+sym.value))
      given.add(String(Math.abs(+sym.value)).replace('.', ','));
    // valeur écrite comme l'énoncé l'écrit (« 0,3 », « 2/3 ») : ses nombres tels quels
    else if (sym.value != null) {
      try {
        atomsOf(parse(String(sym.value), table), table).nums.forEach((n) => given.add(n));
      } catch {
        /* illisible */
      }
    }
    for (const ex of sym.exact || []) {
      try {
        atomsOf(parse(String(ex), table), table).nums.forEach((n) => given.add(n));
      } catch {
        /* illisible */
      }
    }
  }
  // les nombres des FORMULES DU COURS de l'exercice (preds) : le 1 de « 1 − p », le 2 de « n(n + 1)/2 »…
  for (const pr of model?.preds || []) {
    try {
      atomsOf(parse(String(pr.line), table), table).nums.forEach((n) => given.add(n));
    } catch {
      /* illisible */
    }
  }

  // keyboard.numbers = "written" : seulement les nombres DÉJÀ ÉCRITS sur la feuille (départ et lignes gardées) et ceux
  // de l'énoncé — jamais ceux de la prochaine ligne du corrigé, qui souffleraient un résultat. "expected" (défaut
  // historique) : ajoute aussi ceux de la ligne attendue.
  // "source" : seulement les nombres de la ligne source (la dernière, ou celle que l'élève a choisie) — les anciens
  // termes déjà transformés ne surchargent plus le clavier
  // la source peut être une ligne d'énoncé : plusieurs formules et nombres (liste d'AST)
  const srcList = Array.isArray(sourceAst)
    ? sourceAst.filter(Boolean)
    : [sourceAst || lastAst].filter(Boolean);
  const src = srcList[0] ?? null;
  const written =
    K.numbers === 'source'
      ? new Set(srcList.flatMap((a) => [...atomsOf(a, table).nums]))
      : new Set(seen.nums);
  if (K.numbers === 'written')
    for (const a of [startAst, ...lineAsts])
      if (a) atomsOf(a, table).nums.forEach((n) => written.add(n));
  const numbers = [
    ...new Set([
      ...(K.numbers === 'written' || K.numbers === 'source' ? [] : want.nums),
      ...written,
      ...given,
    ]),
  ];
  if (sort !== 'none') numbers.sort((a, b) => (sort === 'desc' ? nv(b) - nv(a) : nv(a) - nv(b)));
  let ids = [...new Set([...want.ids, ...seen.ids, ...Object.keys(model?.symbols || {})])]
    .filter(Boolean)
    .sort();
  // vecteurs : TOUS les vecteurs formés avec les points en jeu (AB⃗, BA⃗, AC⃗…), jamais seulement ceux du corrigé
  const vm = table.lex?.vectorMark;
  if (vm && ids.some((n) => vectorPoints(n, table)?.length)) {
    const pts = [...new Set(ids.flatMap((n) => vectorPoints(n, table) || []))].sort();
    const pairs = pts.flatMap((a) => pts.filter((b) => b !== a).map((b) => a + b + vm));
    ids = [...ids.filter((n) => !vectorPoints(n, table)), ...pairs];
  }
  const ops = new Set([
    ...always,
    ...want.ops,
    ...(model?.kernel?.goal?.kind === 'solve' ? K.solve || [] : []),
  ]);
  // analyse : la marque de dérivée reste au clavier tant que le départ en a une (dériver un morceau à la fois)
  const prime = table.lex?.prime;
  if (prime && startAst && atomsOf(startAst, table).ops.has(prime)) ops.add(prime);
  if (expectedText && (want.ops.has('−') || want.ops.has('+')))
    withMinus.forEach((o) => ops.add(o));
  // familles : une touche utile ou déjà sur la ligne → toute sa famille, pour ne pas souffler laquelle choisir
  for (const g of K.groups || [])
    if (g.some((o) => ops.has(o) || seen.ops?.has(o))) g.forEach((o) => ops.add(o));
  const ordered = [
    ...OP_ORDER.filter((o) => ops.has(o)),
    ...[...ops].filter((o) => !OP_ORDER.includes(o)),
  ];
  const C = K.chunks || { from: ['start', 'last'], max: 8 };
  const srcs = [];
  // « lines » : ce que l'élève a sous les yeux, de la ligne la plus récente à la plus ancienne
  if ((C.from || []).includes('source') && src) srcs.push(...srcList);
  if ((C.from || []).includes('lines')) srcs.push(...[...lineAsts].reverse());
  if ((C.from || []).includes('start') && !srcs.includes(startAst)) srcs.push(startAst);
  if ((C.from || []).includes('last') && lastAst !== startAst && !srcs.includes(lastAst))
    srcs.push(lastAst);
  const chunks = C.max === 0 ? [] : chunksOf(engine, srcs, C.max ?? 8);
  // la touche fraction ne doit pas souffler QUAND diviser : avec frac = 'path', elle est là du début à la fin
  // dès que le corrigé contient une fraction quelque part ; avec 'next' (défaut), seulement pour la ligne attendue
  let frac = want.frac;
  if (K.frac === 'path' && !frac)
    frac = [startAst, ...pathAsts].some((a) => a && atomsOf(a, table).frac);
  // pour isoler une inconnue (but « solve »), diviser les deux membres par un coefficient est toujours possible :
  // une relation dont un membre
  // contient un produit à coefficient numérique ≠ ±1 (−8x ≥ −16) appelle la fraction, que le corrigé en ait ou non
  if (
    K.frac === 'path' &&
    !frac &&
    model?.kernel?.goal?.kind === 'solve' &&
    lastAst &&
    relationParts(lastAst, table)
  )
    frac = hasCoefficient(lastAst, table);
  return { ids, numbers, ops: ordered, frac, parens: want.parens, chunks };
}

// ── §10 Outil de genre `value` : « Calculer » propose plusieurs valeurs ──────
// Écriture d'un rationnel comme le moteur l'écrit : « 10 », « −10 », « 2/3 », « −2/3 ».
export function ratText(v) {
  const a = Math.abs(v.p);
  return (v.p < 0 ? '−' : '') + (v.q === 1 ? String(a) : `${a}/${v.q}`);
}
// Écriture décimale d'un rationnel quand elle est FINIE (dénominateur fait de 2 et de 5) : « 0,015 », « −2,5 » ; sinon null.
export function decText(v) {
  let q = v.q,
    k2 = 0,
    k5 = 0;
  while (q % 2 === 0) {
    q /= 2;
    k2++;
  }
  while (q % 5 === 0) {
    q /= 5;
    k5++;
  }
  if (q !== 1) return null;
  const k = Math.max(k2, k5);
  const n = (BigInt(Math.abs(v.p)) * 10n ** BigInt(k)) / BigInt(v.q);
  let t = n.toString().padStart(k + 1, '0');
  if (k) t = `${t.slice(0, -k)},${t.slice(-k)}`.replace(/,?0+$/, '');
  return (v.p < 0 ? '−' : '') + t;
}
// valueChoices(engine, tool, exprText) → { label, values } | null (lettre, relation, pas de valeur exacte)
// ── Réduire en un monôme (cible *monomial) : 12x − 10x → 2x, 3x × 4x → 12x², avec des distracteurs ──
// Monôme = { c: rationnel, lit: { lettre: exposant } }. Sources : value, sign, neighbors, swap_op (signes ignorés ou
// somme ↔ produit), power (exposant ajouté / oublié), letter (lettre oubliée).
function monomialOf(n, table, R) {
  const one = (c) => ({ c, lit: {} });
  const mul = (a, b) => {
    const lit = { ...a.lit };
    for (const [k, e] of Object.entries(b.lit)) lit[k] = (lit[k] || 0) + e;
    return { c: Q.mul(a.c, b.c), lit };
  };
  if (n.t === 'Num') return one(num(n.p, n.q));
  if (n.t === 'Sym')
    return table.syntax.constants[n.name] ? null : { c: num(1), lit: { [n.name]: 1 } };
  if (n.t === R.neg) {
    const m = monomialOf(n.k[0], table, R);
    return m && { c: Q.neg(m.c), lit: m.lit };
  }
  if (n.t === R.mul) {
    let acc = one(num(1));
    for (const k of n.k) {
      const m = monomialOf(k, table, R);
      if (!m) return null;
      acc = mul(acc, m);
    }
    return acc;
  }
  if (n.t === R.pow && n.k[1].t === 'Num' && n.k[1].q === 1 && n.k[1].p >= 0 && n.k[1].p <= 6) {
    const m = monomialOf(n.k[0], table, R);
    if (!m) return null;
    let acc = one(num(1));
    for (let i = 0; i < n.k[1].p; i++) acc = mul(acc, m);
    return acc;
  }
  if (n.t === R.div) {
    const a = monomialOf(n.k[0], table, R),
      b = monomialOf(n.k[1], table, R);
    if (!a || !b || Object.keys(b.lit).length || b.c.p === 0) return null;
    return { c: Q.div(a.c, b.c), lit: a.lit };
  }
  return null;
}
const litKey = (lit) =>
  Object.keys(lit)
    .filter((k) => lit[k])
    .sort()
    .map((k) => `${k}^${lit[k]}`)
    .join('·');
const SUPS = { 2: '²', 3: '³' };
function monoText(m) {
  const lits = Object.keys(m.lit)
    .filter((k) => m.lit[k])
    .sort()
    .map((k) => k + (m.lit[k] === 1 ? '' : SUPS[m.lit[k]] || `^${m.lit[k]}`))
    .join('');
  if (m.c.p === 0) return '0';
  if (!lits) return ratText(m.c);
  if (m.c.q !== 1) return `${ratText(m.c)} × ${lits}`;
  return (m.c.p === 1 ? '' : m.c.p === -1 ? '−' : ratText(m.c)) + lits;
}
function monomialChoices(engine, tool, exprText) {
  const table = engine.table,
    R = roles(table);
  let ast;
  try {
    ast = parse(exprText, table);
  } catch {
    return null;
  }
  if (relationParts(ast, table)) return null;
  const L = light(ast, table);
  const isSum = L.t === R.add;
  const parts = isSum ? L.k.map((k) => monomialOf(k, table, R)) : [monomialOf(L, table, R)];
  if (parts.some((m) => !m)) return null;
  if (!parts.some((m) => litKey(m.lit))) return null; // sans lettre : c'est « Calculer »
  const keys = new Set(parts.map((m) => litKey(m.lit)));
  if (keys.size !== 1) return null; // termes non semblables : pas UN monôme
  if (!isSum && L.t !== R.mul && L.t !== R.pow && L.t !== R.div) return null; // rien à calculer
  const lit = parts[0].lit;
  const v = { c: parts.reduce((a, m) => Q.add(a, m.c), num(0)), lit };
  // déjà réduit (9x, 12x, x², −3x) : rien à calculer, donc rien à proposer
  const flat = (t) =>
    String(t)
      .replace(/[\s()]/g, '')
      .replace(/×/g, '')
      .replace(/÷/g, '/');
  if (flat(monoText(v)) === flat(toText(ast, table))) return null;
  const C = tool.choices || {};
  const size = C.size ?? 5;
  const seen = new Set([monoText(v)]),
    pool = [];
  const add = (m) => {
    if (!m || !Number.isFinite(m.c.p / m.c.q)) return;
    const t = monoText(m);
    if (!seen.has(t)) {
      seen.add(t);
      pool.push(m);
    }
  };
  const bump = (l, d) =>
    Object.fromEntries(Object.entries(l).map(([k, e]) => [k, Math.max(1, e + d)]));
  // pour un produit : les facteurs (coefficients et lettres) lus dans l'écriture d'origine
  const top = (n) =>
    n.t === R.mul
      ? n.k.flatMap((k) => (k.t === R.mul && !k._closed && n.k.length === 1 ? top(k) : [k]))
      : [n];
  const factors =
    !isSum && L.t === R.mul && ast.t === R.mul
      ? top(ast).map((k) => monomialOf(k, table, R))
      : null; // l'écriture d'origine : 3x × 4x → [3x, 4x]
  for (const src of C.sources || []) {
    if (src === 'sign') add({ c: Q.neg(v.c), lit });
    else if (src === 'neighbors') for (const d of [1, -1]) add({ c: Q.add(v.c, num(d)), lit });
    else if (src === 'letter') add({ c: v.c, lit: {} });
    else if (src === 'power') {
      add({ c: v.c, lit: bump(lit, 1) });
      if (Object.values(lit).some((e) => e > 1)) add({ c: v.c, lit: bump(lit, -1) });
    } else if (src === 'swap_op') {
      if (isSum) {
        add({ c: parts.reduce((a, m) => Q.add(a, num(Math.abs(m.c.p), m.c.q)), num(0)), lit }); // signes ignorés : 12x − 10x → 22x
        const prod = parts.reduce(
          (a, m) => ({
            c: Q.mul(a.c, m.c),
            lit: Object.fromEntries(
              Object.entries(m.lit).map(([k, e]) => [k, (a.lit[k] || 0) + e]),
            ),
          }),
          { c: num(1), lit: {} },
        );
        add(prod); // somme lue comme un produit : 120x²
      } else if (factors) {
        add({ c: factors.reduce((a, m) => Q.add(a, m.c), num(0)), lit }); // coefficients ajoutés : 3x × 4x → 7x²
        add({
          c: v.c,
          lit: Object.fromEntries(
            Object.keys(lit).map((k) => [k, Math.max(...factors.map((m) => m.lit[k] || 0))]),
          ),
        }); // exposants pas ajoutés : 12x
      }
    }
  }
  const vals = [v, ...pool.slice(0, size - 1)];
  const deg = (m) => Object.values(m.lit).reduce((a, e) => a + e, 0);
  if (C.sort !== 'none')
    vals.sort((a, b) => (C.sort === 'desc' ? -1 : 1) * (Q.cmp(a.c, b.c) || deg(a) - deg(b)));
  return { label: tool.label, values: vals.map(monoText) };
}

export function valueChoices(engine, tool, exprText) {
  if (tool.target === '*monomial') return monomialChoices(engine, tool, exprText);
  const table = engine.table,
    R = roles(table);
  let ast;
  try {
    ast = parse(exprText, table);
  } catch {
    return null;
  }
  if (relationParts(ast, table)) return null;
  if ([...symbols(ast)].some((s) => table.syntax.constants[s] == null)) return null;
  const exact = (n) => {
    try {
      return exactValue(n, table);
    } catch {
      return null;
    }
  };
  const v = exact(ast);
  if (!v) return null;
  const flatN = (t) =>
    String(t)
      .replace(/[\s()]/g, '')
      .replace(/÷/g, '/');
  // écriture décimale (si elle est finie) quand le calcul contient un décimal (0,3 × 0,05 → 0,015), ou pour convertir
  // une fraction irréductible à écriture décimale finie (3 ÷ 20 → 0,15) ; une fraction à simplifier reste en fractions
  const hasDec = (n) => (n.t === 'Num' ? !!n.dec : (n.k || []).some(hasDec));
  const irreducible = flatN(ratText(v)) === flatN(toText(ast, table));
  // … et pour un quotient par 10, 100, 1000 (30 ÷ 100 → 0,3 : pourcentages, fractions décimales)
  const evalOf = (n) => table.nodes[n.t]?.eval;
  const byPow10 =
    evalOf(ast) === 'div' &&
    ast.k[1].t === 'Num' &&
    ast.k[1].q === 1 &&
    /^10+$/.test(String(ast.k[1].p));
  const decimal = (hasDec(ast) || byPow10 || (irreducible && v.q !== 1)) && decText(v) != null;
  const fmt = (x) => (decimal && decText(x) != null ? decText(x) : ratText(x));
  if (flatN(fmt(v)) === flatN(toText(ast, table))) return null; // « 5 », « (−3) », « 2/15 », « 0,3 » : rien à calculer
  const L = light(ast, table);
  const C = tool.choices || {};
  const size = C.size ?? 5;
  const useful = [],
    pool = [];
  const seen = new Set();
  const add = (x, list) => {
    if (!x || !Number.isFinite(x.p / x.q)) return;
    const k = fmt(x);
    if (seen.has(k)) return;
    seen.add(k);
    list.push(x);
  };
  // un opérateur binaire remplacé (× ↔ +, + ↔ −), à chaque position
  const swaps = () => {
    const out = [];
    const rec = (n, rebuild) => {
      if (!n.k) return;
      if (n.t === R.mul && R.add) out.push(rebuild({ ...n, t: R.add }));
      if (n.t === R.add && R.mul) out.push(rebuild({ ...n, t: R.mul }));
      if (n.t === R.add)
        n.k.forEach((c, i) => {
          const t =
            c.t === R.neg
              ? c.k[0]
              : c.t === 'Num' && c.p < 0
                ? num(-c.p, c.q)
                : { t: R.neg, k: [c] };
          out.push(rebuild({ ...n, k: n.k.map((d, j) => (j === i ? t : d)) }));
        });
      n.k.forEach((c, i) =>
        rec(c, (r) => rebuild({ ...n, k: n.k.map((d, j) => (j === i ? r : d)) })),
      );
    };
    rec(L, (r) => r);
    return out;
  };
  // voisins dans la table : un facteur entier d'un produit décalé de ±1 (2 × 5 → 2 × 6, 3 × 5, 2 × 4, 1 × 5)
  const tableNeighbors = () => {
    const out = [];
    const rec = (n, rebuild) => {
      if (!n.k) return;
      if (n.t === R.mul) {
        for (const d of [1, -1])
          n.k.forEach((c, i) => {
            if (c.t === 'Num' && c.q === 1 && Math.abs(c.p) + d * Math.sign(c.p || 1) >= 1) {
              out.push(
                rebuild({
                  ...n,
                  k: n.k.map((e, j) => (j === i ? num(c.p + d * Math.sign(c.p || 1)) : e)),
                }),
              );
            }
          });
      }
      n.k.forEach((c, i) =>
        rec(c, (r) => rebuild({ ...n, k: n.k.map((e, j) => (j === i ? r : e)) })),
      );
    };
    rec(L, (r) => r);
    return out;
  };
  for (const src of C.sources || []) {
    if (src === 'value') add(v, useful);
    else if (src === 'table') for (const c of tableNeighbors()) add(exact(c), pool);
    else if (src === 'traps')
      for (const c of rewriteAll(L, engine, {}, 'trap')) add(exact(c.ast), pool);
    else if (src === 'swap_op') for (const c of swaps()) add(exact(c), pool);
    else if (src === 'sign') add(Q.neg(v), pool);
    else if (src === 'neighbors') for (const d of [1, -1, 10, -10]) add(Q.add(v, num(d)), pool);
    else if (src === 'operands') {
      const walk = (n) => {
        if (n.t === 'Num') add(num(n.p, n.q), pool);
        else if (n.k) n.k.forEach(walk);
      };
      walk(L);
    }
  }
  if (!useful.length) add(v, useful); // la bonne valeur est toujours là
  const keep = useful.slice(0, 1);
  const vals = [
    ...keep,
    ...pool.filter((x) => !keep.some((k) => Q.eq(k, x))).slice(0, size - keep.length),
  ];
  if (C.sort !== 'none') vals.sort((a, b) => (C.sort === 'desc' ? Q.cmp(b, a) : Q.cmp(a, b)));
  return { label: tool.label, values: vals.map(fmt) };
}

// Deux opérandes voisins qui s'annulent (outils de genre neutral) : a + (−a) → 0, a × 1/a → 1.
// group : 'sum' | 'prod' ; a, b : textes des deux opérandes (avec leur signe pour une somme : « 4x² », « − 4x² »).
// → { key, label, identity } ou null. Le repérage se fait sur les motifs du pack, jamais dans l'interface.
export function neutralPairFor(engine, group, a, b) {
  const table = engine.table,
    R = roles(table);
  const target = group === 'sum' ? R.add : group === 'prod' ? R.mul : null;
  const tools = (engine.tools || []).filter((t) => t.kind === 'neutral' && t.target === target);
  if (!tools.length) return null;
  // chaque opérande en forme légère (un terme garde son signe : « − 4x² » → Neg)
  let A, B;
  try {
    const signed = (x) => {
      const t = x.trim().replace(/^\+\s*/, '');
      return /^[−-]/.test(t) ? `−(${t.replace(/^[−-]\s*/, '')})` : t;
    };
    A = light(parse(group === 'sum' ? signed(a) : a, table), table);
    B = light(parse(group === 'sum' ? signed(b) : b, table), table);
  } catch {
    return null;
  }
  // un motif P(p, q) : on lie ses variables sur un opérande, on instancie l'autre moitié et on compare en forme
  // légère — « (x + 1) − (x + 1) » est reconnu même si −(x + 1) se distribue.
  const half = (p, q, X, Y) =>
    match(p, X, table).some((bb) => {
      try {
        return keyOf(light(instantiate(q, bb), table)) === keyOf(Y);
      } catch {
        return false;
      }
    });
  for (const t of tools) {
    if (
      t.pairs.some(
        ([p, q]) => half(p, q, A, B) || half(q, p, A, B) || half(p, q, B, A) || half(q, p, B, A),
      )
    )
      return { key: t.key, label: t.label, identity: t.identity };
  }
  return null;
}

// Un produit avec un coefficient numérique différent de ±1 (8x, −8x, 2/3 × x) quelque part dans l'arbre.
function hasCoefficient(n, table) {
  const R = roles(table);
  let found = false;
  const go = (m) => {
    if (found || isLeaf(m)) return;
    if (
      m.t === R.mul &&
      m.k.some((c) => c.t === 'Num' && !(Math.abs(c.p) === 1 && c.q === 1)) &&
      m.k.some((c) => c.t !== 'Num')
    ) {
      found = true;
      return;
    }
    m.k.forEach(go);
  };
  go(n);
  return found;
}
